import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import PrintModal from '../../components/PrintModal';
import VerificaOrdineGo, { OrdineEstratto, ArticoloOpzione, NegozioOpzione } from './VerificaOrdineGo';
import { apiFetch, apiDownload } from '../../lib/api';
import { formatNumber } from '../../utils/formatters';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './ImportOrdiniGoDettaglioPage.api';

type StatoImport = 'CARICATO' | 'IN_ELABORAZIONE' | 'ELABORATO' | 'ERRORE';
type TipoFile = 'XML' | 'TXT' | 'CSV' | 'XLS' | 'PDF';
type StatoFile = 'CARICATO' | 'ELABORATO' | 'ERRORE';

interface FileRow {
  id: number;
  nomeFile: string;
  tipoFile: TipoFile;
  dimensioneByte: number;
  hashSha256: string;
  gcsPath: string | null;
  stato: StatoFile;
  erroreMessaggio: string | null;
  ordiniEstratti: number;
  righeEstratte: number;
  createdAt: string;
}

interface Caricamento {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: StatoImport;
  utenteName: string;
  totaleFile: number;
  fileElaborati: number;
  fileErrore: number;
  ordiniCreati: number;
  righeCreate: number;
  righeNonRisolte: number;
  createdAt: string;
  insegna: { id: number; codice: string; nome: string };
  file: FileRow[];
  ordiniInviati: number;
  origine?: 'MANUALE' | 'DRIVE';
}

interface EsitoElaborazione {
  fileElaborati: number;
  fileErrore: number;
  fileSaltati: { nomeFile: string; motivo: string }[];
  ordiniTotali: number;
  righeTotali: number;
  righeNonRisolte: number;
  codiciInsegnaImparati: number;
  articoliInIndice: number;
  clientiInsegna: number;
  avvisi: string[];
  quadratura: { ordiniAttesi: number; ordiniEstratti: number; totaleAtteso: number; mancanti: string[]; eccedenti: string[]; quadra: boolean } | null;
  ordiniConfermatiAuto?: number;
}

interface LogRecord {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

// Documento che partirebbe verso GO per un ordine, o i motivi per cui non può partire
interface AnteprimaGo {
  documento: {
    causale_documento: string;
    riferimento: string;
    cliente: string;
    indirizzo?: string;
    data_documento: string;
    data_consegna?: string;
    note?: string;
    righe: { articolo: string; quantita: number; note?: string }[];
  } | null;
  problemi: string[];
}

interface EsitoInvioGo {
  daInviare: number;
  inviati: number;
  falliti: number;
  messaggio?: string;
  dettagli: {
    numeroOrdine: string; ok: boolean; progressivo?: string; errore?: string;
    // valorizzati solo quando il numero era già speso in GO
    riferimento?: string; riferimentoOccupato?: string | null;
  }[];
}

interface EsitoRilevazioneGo {
  daRilevare: number;
  rilevati: number;
  falliti: number;
  dettagli: { numeroOrdine: string; ok: boolean; errore?: string }[];
}

const somma = (valori: (string | number | null | undefined)[]) =>
  valori.reduce<number>((s, v) => s + (v === null || v === undefined || v === '' ? 0 : Number(v)), 0);

const STATO_LABEL: Record<StatoImport, { label: string; variant: string }> = {
  CARICATO:        { label: 'Da elaborare', variant: 'info' },
  IN_ELABORAZIONE: { label: 'In elaborazione', variant: 'warning' },
  ELABORATO:       { label: 'Elaborato', variant: 'success' },
  ERRORE:          { label: 'Errore', variant: 'danger' },
};

const STATO_ORDINE: Record<string, { label: string; variant: string }> = {
  DA_VERIFICARE: { label: 'Da verificare', variant: 'warning' },
  VERIFICATO:    { label: 'Verificato',    variant: 'success' },
  INVIATO_GO:    { label: 'Inviato a GO',  variant: 'info' },
  ERRORE_GO:     { label: 'Errore invio',  variant: 'danger' },
  SCARTATO:      { label: 'Scartato',      variant: 'neutral' },
};

// Che ruolo ha ogni formato nel flusso: l'XLS non porta righe, il PDF è una
// scansione letta con l'OCR. Dirlo qui evita che l'operatore si aspetti da un
// file quello che quel file non contiene.
const RUOLO_FILE: Record<TipoFile, string> = {
  XML: 'testate e righe',
  TXT: 'testate e righe',
  CSV: 'testate e righe',
  XLS: 'solo testate — riscontro totali',
  PDF: 'scansione — letto con OCR',
};

function fmtData(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('it-IT');
}

function fmtDataOra(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
}

function fmtDimensione(byte: number): string {
  if (byte < 1024) return `${byte} B`;
  if (byte < 1024 * 1024) return `${(byte / 1024).toFixed(1)} KB`;
  return `${(byte / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImportOrdiniGoDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [caricamento, setCaricamento] = useState<Caricamento | null>(null);
  const [log, setLog] = useState<LogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [eliminando, setEliminando] = useState(false);
  const [stampando, setStampando] = useState(false);
  const [stampaOrdiniAperta, setStampaOrdiniAperta] = useState(false);
  const [ordini, setOrdini] = useState<OrdineEstratto[]>([]);
  const [elaborando, setElaborando] = useState(false);
  const [esito, setEsito] = useState<EsitoElaborazione | null>(null);
  const [ordineAperto, setOrdineAperto] = useState<number | null>(null);
  const [articoli, setArticoli] = useState<ArticoloOpzione[]>([]);
  const [negozi, setNegozi] = useState<NegozioOpzione[]>([]);

  // invio a GO: modale con anteprima per ordine, poi esito
  const [invioAperto, setInvioAperto] = useState(false);
  const [anteprime, setAnteprime] = useState<Record<number, AnteprimaGo>>({});
  const [anteprimeInCorso, setAnteprimeInCorso] = useState(false);
  const [inviando, setInviando] = useState(false);
  const [esitoInvio, setEsitoInvio] = useState<EsitoInvioGo | null>(null);
  const [erroreInvio, setErroreInvio] = useState('');
  const [rilevando, setRilevando] = useState(false);
  const [messaggioRilevazione, setMessaggioRilevazione] = useState('');

  const canDelete = can('bop_go.import_ordini_smartix', 'delete');
  const canEdit = can('bop_go.import_ordini_smartix', 'edit');

  const ordiniVerificati = ordini.filter(o => o.stato === 'VERIFICATO');
  const ordiniInGo = ordini.filter(o => o.stato === 'INVIATO_GO');
  const ordiniValidi = ordini.filter(o => o.stato !== 'SCARTATO');
  // Totali del caricamento: quanto dichiaravano i file e quanto GO ha registrato
  const totaleDaFile = somma(ordiniValidi.map(o => o.totaleDichiarato));
  const ordiniRilevati = ordiniInGo.filter(o => o.goRilevatoAt);
  const totaleInGo = somma(ordiniRilevati.map(o => o.goImponibile));
  // Confronto a parità di ordini: il file contro ciò che GO ha calcolato con il proprio listino
  const totaleFileRilevati = somma(ordiniRilevati.map(o => o.totaleDichiarato));
  const importoDiverso = (o: OrdineEstratto) =>
    !!o.goRilevatoAt && o.goImponibile !== null && o.totaleDichiarato !== null && o.totaleDichiarato !== undefined
    && Math.abs(Number(o.goImponibile) - Number(o.totaleDichiarato)) > 0.005;
  const righeDiverse = (o: OrdineEstratto) => !!o.goRilevatoAt && o.goRighe !== null && o.goRighe !== o.righeTotali;
  const ordiniConDifferenze = ordiniRilevati.filter(o => importoDiverso(o) || righeDiverse(o));
  // Nessun ordine riletto dichiara un totale: il raffronto sugli importi non è
  // possibile, e il riepilogo deve dirlo invece di far credere che sia riuscito.
  const nessunImportoDichiarato = ordiniRilevati.length > 0
    && ordiniRilevati.every(o => o.totaleDichiarato === null || o.totaleDichiarato === undefined);
  const differenzaImporto = totaleInGo - totaleFileRilevati;

  useEffect(() => {
    void carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function carica() {
    if (!id) return;
    setLoading(true);
    setErrore('');
    try {
      // @api GET /api/bop-go/import-ordini/:id -> {"success":true,"data":{"id":1,"societaId":1,"etichetta":"...","note":null,"stato":"CARICATO","utenteName":"...","totaleFile":3,"fileElaborati":0,"fileErrore":0,"ordiniCreati":0,"righeCreate":0,"righeNonRisolte":0,"createdAt":"...","insegna":{"id":1,"codice":"BC","nome":"Bricocenter"},"file":[{"id":1,"nomeFile":"ordini.xml","tipoFile":"XML","dimensioneByte":12345,"hashSha256":"...","gcsPath":"...","stato":"CARICATO","erroreMessaggio":null,"ordiniEstratti":0,"righeEstratte":0,"createdAt":"..."}]}}
      const res = await apiFetch<{ success: boolean; data: Caricamento }>(`/api/bop-go/import-ordini/${id}`);
      setCaricamento(res.data);

      // @api GET /api/bop-go/import-ordini/:id/log -> {"success":true,"data":[{"id":1,"utenteName":"...","azione":"Creazione caricamento","dettaglio":"...","createdAt":"..."}]}
      const resLog = await apiFetch<{ success: boolean; data: LogRecord[] }>(`/api/bop-go/import-ordini/${id}/log`);
      setLog(resLog.data || []);

      if (res.data.stato !== 'CARICATO') await caricaOrdini();
    } catch (err: any) {
      setErrore(err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }

  async function caricaOrdini() {
    if (!id) return;
    try {
      // @api GET /api/bop-go/import-ordini/:id/ordini -> {"success":true,"data":[{"id":1,"numeroOrdine":"26008635","codiceNegozio":"0032","nomeNegozio":"CINISELLO","cittaNegozio":"CINISELLO BALSAMO","nomeDeposito":"DEPOSITO RIVALTA KTN","dataConsegnaDa":"...","totaleDichiarato":"72.930","note":null,"matchNegozio":"NESSUNO","stato":"DA_VERIFICARE","righeTotali":2,"righeNonRisolte":2,"righe":[],"cliente":null,"filiale":null}]}
      const res = await apiFetch<{ success: boolean; data: OrdineEstratto[] }>(`/api/bop-go/import-ordini/${id}/ordini`);
      setOrdini(res.data || []);

      // Opzioni per la correzione: si caricano una volta sola, sono le stesse
      // per tutti gli ordini del caricamento.
      if (articoli.length === 0) {
        // @api GET /api/bop-go/import-ordini/:id/articoli-smartix?q= -> {"success":true,"data":[{"id":48,"codice":"XST-SCOLAC60","descrizioneBase":"...","ean":"8053017891811"}]}
        const resArt = await apiFetch<{ success: boolean; data: ArticoloOpzione[] }>(`/api/bop-go/import-ordini/${id}/articoli-smartix`);
        setArticoli(resArt.data || []);
      }
      if (negozi.length === 0) {
        // @api GET /api/bop-go/import-ordini/:id/negozi -> {"success":true,"data":[{"id":21,"codice":"NOM00018","ragioneSociale":"Bricocenter Italia spa","filiali":[{"id":3,"nome":"CINISELLO","citta":"CINISELLO BALSAMO"}]}]}
        const resNeg = await apiFetch<{ success: boolean; data: NegozioOpzione[] }>(`/api/bop-go/import-ordini/${id}/negozi`);
        setNegozi(resNeg.data || []);
      }
    } catch (err: any) {
      setErrore(err.message || 'Errore nel recupero degli ordini');
    }
  }

  async function elabora() {
    if (!id) return;
    setElaborando(true);
    setErrore('');
    setEsito(null);
    try {
      // @api POST /api/bop-go/import-ordini/:id/elabora -> {"success":true,"data":{"fileElaborati":4,"fileErrore":0,"fileSaltati":[],"ordiniTotali":37,"righeTotali":112,"righeNonRisolte":0,"codiciInsegnaImparati":0,"articoliInIndice":0,"clientiInsegna":0,"avvisi":[],"quadratura":{"ordiniAttesi":37,"ordiniEstratti":37,"totaleAtteso":4154.79,"mancanti":[],"quadra":true}}}
      const res = await apiFetch<{ success: boolean; data: EsitoElaborazione }>(
        `/api/bop-go/import-ordini/${id}/elabora`, { method: 'POST' }
      );
      setEsito(res.data);
      await carica();
      await caricaOrdini();
    } catch (err: any) {
      setErrore(err.message || "Errore nell'estrazione");
      await carica();
    } finally {
      setElaborando(false);
    }
  }

  // Apre la modale e prepara l'anteprima di ogni ordine verificato: si vede
  // cosa partirebbe (cliente e filiale GO, righe) e cosa manca, prima di toccare GO.
  async function apriInvio() {
    setInvioAperto(true);
    setEsitoInvio(null);
    setErroreInvio('');
    setAnteprime({});
    setAnteprimeInCorso(true);
    const raccolte: Record<number, AnteprimaGo> = {};
    for (const o of ordiniVerificati) {
      try {
        // @api GET /api/bop-go/import-ordini/:id/ordini/:ordineId/anteprima-go -> {"success":true,"data":{"documento":{"causale_documento":"C1S","riferimento":"LEROY MERLIN 041 63895032","cliente":"00000038","indirizzo":"00000041","data_documento":"2026-09-04T00:00:00.000Z","data_consegna":"2026-09-10T00:00:00.000Z","righe":[{"articolo":"XST-SCP90","quantita":2}]},"problemi":[]}}
        const res = await apiFetch<{ success: boolean; data: AnteprimaGo }>(
          `/api/bop-go/import-ordini/${id}/ordini/${o.id}/anteprima-go`
        );
        raccolte[o.id] = res.data;
      } catch (err: any) {
        raccolte[o.id] = { documento: null, problemi: [err.message || 'anteprima non disponibile'] };
      }
      setAnteprime({ ...raccolte });
    }
    setAnteprimeInCorso(false);
  }

  function chiudiInvio() {
    if (inviando) return;
    setInvioAperto(false);
  }

  async function inviaGo() {
    if (!id) return;
    setInviando(true);
    setErroreInvio('');
    try {
      // @api POST /api/bop-go/import-ordini/:id/invia-go -> {"success":true,"data":{"daInviare":20,"inviati":20,"falliti":0,"dettagli":[{"numeroOrdine":"63895032","ok":true,"progressivo":"12345"}]}}
      const res = await apiFetch<{ success: boolean; data: EsitoInvioGo }>(
        `/api/bop-go/import-ordini/${id}/invia-go`, { method: 'POST' }
      );
      setEsitoInvio(res.data);
    } catch (err: any) {
      setErroreInvio(err.message || "Errore nell'invio a GO");
    } finally {
      setInviando(false);
      await caricaOrdini();
      await carica();
    }
  }

  // Rilegge da GO righe e importi di ogni ordine inviato: due chiamate per
  // ordine, quindi su richiesta e non a ogni apertura della pagina.
  async function rilevaDaGo() {
    if (!id) return;
    setRilevando(true);
    setMessaggioRilevazione('');
    try {
      // @api POST /api/bop-go/import-ordini/:id/rileva-go -> {"success":true,"data":{"daRilevare":20,"rilevati":20,"falliti":0,"dettagli":[{"numeroOrdine":"63895032","ok":true,"goNumeroDocumento":"SM 2318","goRighe":2,"goImponibile":190.5,"goTotale":232.41,"goSituazione":"inserito"}]}}
      const res = await apiFetch<{ success: boolean; data: EsitoRilevazioneGo }>(
        `/api/bop-go/import-ordini/${id}/rileva-go`, { method: 'POST' }
      );
      const falliti = res.data.dettagli.filter(d => !d.ok);
      setMessaggioRilevazione(
        `${res.data.rilevati} ordini riletti da GO` +
        (falliti.length ? ` — ${falliti.length} non trovati: ${falliti.map(d => d.numeroOrdine).join(', ')}` : '')
      );
      await caricaOrdini();
      await carica();
    } catch (err: any) {
      setMessaggioRilevazione(err.message || 'Errore nella rilettura da GO');
    } finally {
      setRilevando(false);
    }
  }

  // Il PDF si genera con Puppeteer lato server: su un lotto da venti ordini
  // sono alcuni secondi, quindi il bottone deve mostrare che sta lavorando.
  async function stampaProspetto() {
    setStampando(true);
    try {
      // @api GET /api/bop-go/import-ordini/:id/prospetto.pdf -> file binario
      await apiDownload(`/api/bop-go/import-ordini/${id}/prospetto.pdf`, `prospetto-caricamento-${id}.pdf`);
    } catch (err: any) {
      setErrore(err.message || 'Errore nella generazione del prospetto');
    } finally {
      setStampando(false);
    }
  }

  async function scaricaFile(f: FileRow) {
    try {
      // @api GET /api/bop-go/import-ordini/:id/file/:fileId -> file binario
      await apiDownload(`/api/bop-go/import-ordini/${id}/file/${f.id}`, f.nomeFile);
    } catch (err: any) {
      setErrore(err.message || 'Errore nel download del file');
    }
  }

  async function elimina() {
    if (!caricamento) return;
    const quanti = ordini.length > 0 ? `, ${ordini.length} ordini estratti` : '';
    if (!window.confirm(`Eliminare il caricamento "${caricamento.etichetta}" con i suoi ${caricamento.file.length} file${quanti}?`)) return;
    setEliminando(true);
    try {
      // @api DELETE /api/bop-go/import-ordini/:id -> {"success":true}
      await apiFetch(`/api/bop-go/import-ordini/${id}`, { method: 'DELETE' });
      navigate('/bop-go/import-ordini-smartix');
    } catch (err: any) {
      setErrore(err.message || "Errore nell'eliminazione");
      setEliminando(false);
    }
  }

  if (loading) {
    return <Layout title="Caricamento ordini"><div className="bop-empty-state">Caricamento...</div></Layout>;
  }

  if (!caricamento) {
    return (
      <Layout title="Caricamento ordini">
        <BopBackLink to="/bop-go/import-ordini-smartix" label="Torna ai caricamenti" />
        <div className="bop-empty-state">{errore || 'Caricamento non trovato'}</div>
      </Layout>
    );
  }

  const stato = STATO_LABEL[caricamento.stato];
  // Si elimina finché nulla è partito verso GO: cancellare qui un ordine già
  // inviato non lo annullerebbe dall'altra parte.
  const eliminabile = (caricamento.ordiniInviati ?? 0) === 0;

  return (
    <Layout title={`Caricamento ${caricamento.etichetta}`}>
      <BopBackLink to="/bop-go/import-ordini-smartix" label="Torna ai caricamenti" />

      <div className="bop-page-header">
        <h1 className="bop-page-title">{caricamento.etichetta}</h1>
        <div className="bop-flex">
          {ordini.length > 0 && (
            <button className="bop-btn bop-btn-secondary" onClick={stampaProspetto} disabled={stampando}>
              {stampando ? 'Generazione...' : 'Stampa prospetto'}
            </button>
          )}
          {ordiniInGo.length > 0 && (
            <button className="bop-btn bop-btn-secondary" onClick={() => setStampaOrdiniAperta(true)}>
              Stampa ordini ({ordiniInGo.length})
            </button>
          )}
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={elabora} disabled={elaborando}>
              {elaborando ? 'Estrazione in corso...' : ordini.length > 0 ? 'Rielabora file' : 'Elabora file'}
            </button>
          )}
          {canDelete && eliminabile && (
            <button className="bop-btn bop-btn-danger" onClick={elimina} disabled={eliminando || elaborando}>
              {eliminando ? 'Eliminazione...' : 'Elimina caricamento'}
            </button>
          )}
        </div>
      </div>

      {errore && <p className="bop-text-danger">{errore}</p>}

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Riepilogo</h3>
        </div>
        <div className="bop-card__body">
          <table className="bop-table">
            <tbody>
              <tr><td>Insegna</td><td><strong>{caricamento.insegna.nome}</strong></td></tr>
              <tr><td>Stato</td><td><span className={`bop-badge bop-badge-${stato.variant}`}>{stato.label}</span></td></tr>
              <tr>
                <td>Caricato da</td>
                <td>
                  {caricamento.utenteName} il {fmtDataOra(caricamento.createdAt)}
                  {caricamento.origine === 'DRIVE' && <span className="bop-badge bop-badge-info"> da Google Drive</span>}
                </td>
              </tr>
              {caricamento.note && <tr><td>Note</td><td>{caricamento.note}</td></tr>}
              <tr><td>File</td><td>{caricamento.file.length}</td></tr>
              <tr>
                <td>Ordini estratti</td>
                <td>
                  {caricamento.stato === 'CARICATO'
                    ? <span className="bop-text-muted">estrazione non ancora eseguita</span>
                    : `${caricamento.ordiniCreati} ordini, ${caricamento.righeCreate} righe` +
                      (caricamento.righeNonRisolte ? ` — ${caricamento.righeNonRisolte} righe senza articolo` : '')}
                </td>
              </tr>
              {ordini.length > 0 && (
                <tr>
                  <td>Totale ordini dai file</td>
                  <td>
                    <div className="bop-go-riepilogo__valore">
                      <strong>{formatNumber(totaleDaFile)} €</strong>
                      <span className="bop-text-muted">
                        imponibile ai prezzi dichiarati dalla catena, su {ordiniValidi.length} ordini
                        {ordiniValidi.length < ordini.length && ` (${ordini.length - ordiniValidi.length} scartati esclusi)`}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {ordiniInGo.length > 0 && (
                <tr>
                  <td>Totale registrato in GO</td>
                  <td>
                    {ordiniRilevati.length === 0
                      ? <span className="bop-text-muted">non ancora riletto da GO — usa "Rileva da GO"</span>
                      : (
                        <div className="bop-go-riepilogo__valore">
                          <strong>{formatNumber(totaleInGo)} €</strong>
                          <span className="bop-text-muted">
                            imponibile calcolato da GO con il proprio listino su {ordiniRilevati.length} di {ordiniInGo.length} ordini inviati
                            {' · '}{formatNumber(somma(ordiniRilevati.map(o => o.goTotale)))} € con IVA
                          </span>
                          {ordiniConDifferenze.length > 0 ? (
                            <span className="bop-badge bop-badge-danger" title="Ordini in cui righe o imponibile registrati in GO non coincidono con il file: vedi la tabella sotto">
                              {ordiniConDifferenze.length} {ordiniConDifferenze.length === 1 ? 'ordine diverso' : 'ordini diversi'} dal file
                              {Math.abs(differenzaImporto) > 0.005 && ` · ${differenzaImporto > 0 ? '+' : ''}${formatNumber(differenzaImporto)} €`}
                            </span>
                          ) : nessunImportoDichiarato ? (
                            /* Alcune catene mandano ordini senza prezzi (Eurobrico): il
                               confronto sugli importi non è stato fatto, e dire "coincide"
                               spaccerebbe per verifica riuscita una verifica mai avvenuta. */
                            <span className="bop-badge bop-badge-neutral" title="Il file del cliente non riporta prezzi né totali: si può confrontare solo il numero di righe">
                              righe coincidenti · il file non dichiara importi
                            </span>
                          ) : (
                            <span className="bop-badge bop-badge-success">coincide con il file</span>
                          )}
                        </div>
                      )}
                  </td>
                </tr>
              )}
              {ordini.length > 0 && (
                <tr>
                  <td>Prezzi</td>
                  <td>
                    <span className="bop-text-muted">
                      BOP non trasmette prezzi a GO: le righe partono con solo articolo e quantità e GO applica il listino
                      e le condizioni del cliente. Il totale dai file resta in BOP per il confronto con quanto GO ha calcolato.
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">File del caricamento</h3>
        </div>
        <div className="bop-card__body">
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome file</th>
                <th>Tipo</th>
                <th>Contenuto</th>
                <th className="bop-text-right">Dimensione</th>
                <th>Stato</th>
                <th className="bop-text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {caricamento.file.map(f => (
                <tr key={f.id}>
                  <td>{f.nomeFile}</td>
                  <td><span className="bop-badge bop-badge-info">{f.tipoFile}</span></td>
                  <td className="bop-text-muted">{RUOLO_FILE[f.tipoFile]}</td>
                  <td className="bop-text-right">{fmtDimensione(f.dimensioneByte)}</td>
                  <td>
                    {f.stato === 'ERRORE'
                      ? <span className="bop-badge bop-badge-danger" title={f.erroreMessaggio || ''}>Errore</span>
                      : f.stato === 'ELABORATO'
                        ? <span className="bop-badge bop-badge-success">{f.ordiniEstratti} ordini</span>
                        : <span className="bop-badge bop-badge-info">Da elaborare</span>}
                  </td>
                  <td className="bop-text-right">
                    <BopRowActions
                      actions={[
                        {
                          icon: <RowIcon.Download />,
                          label: 'Scarica',
                          onClick: () => scaricaFile(f),
                          disabled: !f.gcsPath,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {esito && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Esito dell'estrazione</h3>
          </div>
          <div className="bop-card__body">
            <table className="bop-table">
              <tbody>
                <tr>
                  <td>File</td>
                  <td>{esito.fileElaborati} elaborati{esito.fileErrore > 0 && `, ${esito.fileErrore} in errore`}</td>
                </tr>
                <tr>
                  <td>Ordini</td>
                  <td>{esito.ordiniTotali} ordini, {esito.righeTotali} righe</td>
                </tr>
                <tr>
                  <td>Righe agganciate a un articolo</td>
                  <td>
                    {esito.righeTotali - esito.righeNonRisolte} su {esito.righeTotali}
                    {esito.righeNonRisolte > 0 && (
                      <span className="bop-badge bop-badge-warning"> {esito.righeNonRisolte} da risolvere</span>
                    )}
                  </td>
                </tr>
                {(esito.ordiniConfermatiAuto ?? 0) > 0 && (
                  <tr>
                    <td>Confermati automaticamente</td>
                    <td>
                      {esito.ordiniConfermatiAuto} su {esito.ordiniTotali}
                      <span className="bop-text-muted"> — negozio e articoli tutti agganciati; gli altri restano da verificare</span>
                    </td>
                  </tr>
                )}
                {esito.codiciInsegnaImparati > 0 && (
                  <tr>
                    <td>Codici insegna memorizzati</td>
                    <td>{esito.codiciInsegnaImparati}</td>
                  </tr>
                )}
                {esito.quadratura && (
                  <tr>
                    <td>Quadratura col riepilogo</td>
                    <td>
                      {esito.quadratura.ordiniEstratti} estratti su {esito.quadratura.ordiniAttesi} attesi{' '}
                      {esito.quadratura.quadra
                        ? <span className="bop-badge bop-badge-success">quadra</span>
                        : <span className="bop-badge bop-badge-danger">non quadra</span>}
                      {esito.quadratura.mancanti.length > 0 && (
                        <div className="bop-text-muted">mancanti: {esito.quadratura.mancanti.join(', ')}</div>
                      )}
                      {esito.quadratura.eccedenti.length > 0 && (
                        <div className="bop-text-muted">
                          {esito.quadratura.eccedenti.length} ordini estratti non presenti nel riepilogo
                          — probabilmente il tracciato di un altro lotto caricato insieme a questo
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {esito.fileSaltati.length > 0 && (
              <p className="bop-text-muted">
                Non elaborati: {esito.fileSaltati.map(f => `${f.nomeFile} (${f.motivo})`).join(' · ')}
              </p>
            )}
            {esito.avvisi.map((a, i) => (
              <p key={i} className="bop-text-danger">{a}</p>
            ))}
          </div>
        </div>
      )}

      {ordini.length > 0 && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Ordini estratti ({ordini.length})
              {ordini.filter(o => o.stato === 'VERIFICATO').length > 0 && (
                <span className="bop-badge bop-badge-success">
                  {ordini.filter(o => o.stato === 'VERIFICATO').length} verificati
                </span>
              )}
              {ordini.filter(o => o.stato === 'DA_VERIFICARE').length > 0 && (
                <span className="bop-badge bop-badge-warning">
                  {ordini.filter(o => o.stato === 'DA_VERIFICARE').length} da verificare
                </span>
              )}
              {ordini.filter(o => o.stato === 'INVIATO_GO').length > 0 && (
                <span className="bop-badge bop-badge-info">
                  {ordini.filter(o => o.stato === 'INVIATO_GO').length} in GO
                </span>
              )}
              {ordini.filter(o => o.stato === 'ERRORE_GO').length > 0 && (
                <span className="bop-badge bop-badge-danger">
                  {ordini.filter(o => o.stato === 'ERRORE_GO').length} in errore
                </span>
              )}
            </h3>
            <div className="bop-flex">
              {messaggioRilevazione && <span className="bop-text-muted">{messaggioRilevazione}</span>}
              {canEdit && ordiniInGo.length > 0 && (
                <button
                  className="bop-btn bop-btn-secondary bop-btn-sm"
                  onClick={rilevaDaGo}
                  disabled={rilevando || inviando}
                  title="Rilegge da GO righe e importi di ogni ordine inviato"
                >
                  {rilevando ? 'Lettura da GO…' : `Rileva da GO (${ordiniInGo.length})`}
                </button>
              )}
              {canEdit && ordiniVerificati.length > 0 && (
                <button
                  className="bop-btn bop-btn-primary bop-btn-sm"
                  onClick={apriInvio}
                  disabled={elaborando || inviando || rilevando}
                  title="Manda a GO gli ordini verificati, dopo un'anteprima"
                >
                  Invia a GO ({ordiniVerificati.length})
                </button>
              )}
            </div>
          </div>
          <div className="bop-card__body">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Ordine</th>
                  <th>Negozio</th>
                  <th>Consegna</th>
                  <th>Deposito</th>
                  <th className="bop-text-right" title="Dal file della catena">Righe</th>
                  <th className="bop-text-right" title="Imponibile dichiarato dal file">Totale</th>
                  {ordiniInGo.length > 0 && <th className="bop-text-right" title="Righe registrate in GO">Righe GO</th>}
                  {ordiniInGo.length > 0 && <th className="bop-text-right" title="Imponibile registrato in GO">Totale GO</th>}
                  <th>Articoli</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {ordini.map(o => (
                  <React.Fragment key={o.id}>
                    <tr onClick={() => setOrdineAperto(ordineAperto === o.id ? null : o.id)}>
                      <td><strong>{o.numeroOrdine}</strong></td>
                      <td>
                        {o.codiceNegozio} — {o.nomeNegozio || '—'}
                        {o.matchNegozio === 'NESSUNO'
                          ? <span className="bop-badge bop-badge-warning"> non in anagrafica</span>
                          : <span className="bop-badge bop-badge-success"> {o.filiale?.nome || o.cliente?.ragioneSociale}</span>}
                      </td>
                      <td>{fmtData(o.dataConsegnaDa)}</td>
                      <td className="bop-text-muted">{o.nomeDeposito || '—'}</td>
                      <td className="bop-text-right">{o.righeTotali}</td>
                      <td className="bop-text-right">{o.totaleDichiarato != null ? formatNumber(Number(o.totaleDichiarato)) : '—'}</td>
                      {ordiniInGo.length > 0 && (
                        <td className="bop-text-right">
                          {o.goRilevatoAt ? (
                            righeDiverse(o)
                              ? <span className="bop-badge bop-badge-danger" title={`GO ha ${o.goRighe} righe, il file ${o.righeTotali}`}>{o.goRighe} ≠ {o.righeTotali}</span>
                              : o.goRighe
                          ) : '—'}
                        </td>
                      )}
                      {ordiniInGo.length > 0 && (
                        <td className="bop-text-right" title={o.goNumeroDocumento ? `documento GO ${o.goNumeroDocumento}` : undefined}>
                          {o.goRilevatoAt && o.goImponibile !== null ? (
                            importoDiverso(o)
                              ? (
                                <span
                                  className="bop-badge bop-badge-danger"
                                  title={`GO: ${formatNumber(Number(o.goImponibile))} € — file: ${formatNumber(Number(o.totaleDichiarato))} € (${Number(o.goImponibile) - Number(o.totaleDichiarato) > 0 ? '+' : ''}${formatNumber(Number(o.goImponibile) - Number(o.totaleDichiarato))} €)`}
                                >
                                  {formatNumber(Number(o.goImponibile))} ≠ file
                                </span>
                              )
                              : formatNumber(Number(o.goImponibile))
                          ) : '—'}
                        </td>
                      )}
                      <td>
                        {o.righeNonRisolte === 0
                          ? <span className="bop-badge bop-badge-success">tutti agganciati</span>
                          : <span className="bop-badge bop-badge-warning">{o.righeNonRisolte} da risolvere</span>}
                      </td>
                      <td>
                        <span
                          className={`bop-badge bop-badge-${STATO_ORDINE[o.stato]?.variant || 'neutral'}`}
                          title={o.esitoInvio || undefined}
                        >
                          {STATO_ORDINE[o.stato]?.label || o.stato}
                          {o.stato === 'INVIATO_GO' && o.goId && ` · n. ${o.goId}`}
                        </span>
                        {/* In GO questo ordine si chiama diversamente dal file:
                            senza il badge lo si scoprirebbe solo cercandolo. */}
                        {o.goRiferimentoOccupato && (
                          <span
                            className="bop-badge bop-badge-warning"
                            title={`Il numero ${o.goRiferimentoOccupato} era già presente in GO: l'ordine è stato inserito come ${o.goNumero || '—'}`}
                          >
                            rif. {o.goNumero || '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                    {ordineAperto === o.id && (
                      <tr>
                        <td colSpan={ordiniInGo.length > 0 ? 10 : 8}>
                          <VerificaOrdineGo
                            importId={id!}
                            ordine={o}
                            articoli={articoli}
                            negozi={negozi}
                            modificabile={canEdit}
                            onAggiornato={caricaOrdini}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Storico</h3>
        </div>
        <div className="bop-card__body">
          {log.length === 0 ? (
            <div className="bop-empty-state">Nessuna attività registrata</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Data / Ora</th>
                  <th>Utente</th>
                  <th>Azione</th>
                  <th>Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {log.map(r => (
                  <tr key={r.id}>
                    <td>{fmtDataOra(r.createdAt)}</td>
                    <td>{r.utenteName}</td>
                    <td>
                      <span className={`bop-badge ${r.azione === 'File scartato' ? 'bop-badge-warning' : 'bop-badge-info'}`}>
                        {r.azione}
                      </span>
                    </td>
                    <td>{r.dettaglio || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {invioAperto && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--xl">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">
                {esitoInvio ? 'Esito invio a GO' : `Invio a GO — ${ordiniVerificati.length} ordini verificati`}
              </h2>
              <button className="bop-modal__close" onClick={chiudiInvio} aria-label="Chiudi" disabled={inviando}>✕</button>
            </div>
            <div className="bop-modal__body">
              {!esitoInvio ? (
                <>
                  <p className="bop-text-muted">
                    Per ogni ordine è mostrato il documento che partirebbe: cliente e filiale GO, righe con codice articolo e quantità.
                    Gli ordini con problemi non vengono inviati e restano da correggere. I prezzi non si trasmettono: li applica GO.
                  </p>
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Ordine</th>
                        <th>Negozio</th>
                        <th>Cliente GO</th>
                        <th>Filiale GO</th>
                        <th>Riferimento</th>
                        <th className="bop-text-right">Righe</th>
                        <th>Esito anteprima</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniVerificati.map(o => {
                        const a = anteprime[o.id];
                        return (
                          <tr key={o.id}>
                            <td><strong>{o.numeroOrdine}</strong></td>
                            <td>{o.codiceNegozio} — {o.filiale?.nome || o.cliente?.ragioneSociale || o.nomeNegozio || '—'}</td>
                            <td>{a?.documento?.cliente || '—'}</td>
                            <td>{a?.documento?.indirizzo || '—'}</td>
                            <td className="bop-text-muted">{a?.documento?.riferimento || '—'}</td>
                            <td className="bop-text-right">
                              {a?.documento
                                ? `${a.documento.righe.length} · ${a.documento.righe.reduce((s, r) => s + r.quantita, 0)} pz`
                                : '—'}
                            </td>
                            <td>
                              {!a
                                ? <span className="bop-text-muted">…</span>
                                : a.documento
                                  ? <span className="bop-badge bop-badge-success">pronto</span>
                                  : <span className="bop-text-danger">{a.problemi.join('; ')}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {erroreInvio && <p className="bop-text-danger">{erroreInvio}</p>}
                </>
              ) : (
                <>
                  <p>
                    <strong>{esitoInvio.inviati}</strong> inviati, <strong>{esitoInvio.falliti}</strong> falliti su {esitoInvio.daInviare}.
                    {esitoInvio.messaggio && ` ${esitoInvio.messaggio}`}
                  </p>
                  {esitoInvio.dettagli.length > 0 && (
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th>Ordine</th>
                          <th>Esito</th>
                          <th>Dettaglio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {esitoInvio.dettagli.map(d => (
                          <tr key={d.numeroOrdine}>
                            <td><strong>{d.numeroOrdine}</strong></td>
                            <td>
                              {d.ok
                                ? <span className="bop-badge bop-badge-success">inviato</span>
                                : <span className="bop-badge bop-badge-danger">fallito</span>}
                            </td>
                            <td>
                              {d.ok ? (
                                <>
                                  progressivo GO {d.progressivo}
                                  {d.riferimentoOccupato && (
                                    <>
                                      {' — '}
                                      <span className="bop-text-warning">
                                        il numero {d.riferimentoOccupato} era già in GO, inserito come <strong>{d.riferimento}</strong>
                                      </span>
                                    </>
                                  )}
                                </>
                              ) : <span className="bop-text-danger">{d.errore}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
            <div className="bop-modal__footer">
              {!esitoInvio ? (
                <>
                  <button className="bop-btn bop-btn-secondary" onClick={chiudiInvio} disabled={inviando}>Annulla</button>
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={inviaGo}
                    disabled={anteprimeInCorso || inviando || !Object.values(anteprime).some(a => a.documento)}
                  >
                    {inviando
                      ? 'Invio in corso…'
                      : anteprimeInCorso
                        ? 'Preparazione anteprima…'
                        : `Invia ${Object.values(anteprime).filter(a => a.documento).length} ordini a GO`}
                  </button>
                </>
              ) : (
                <button className="bop-btn bop-btn-primary" onClick={chiudiInvio}>Chiudi</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stampa cumulativa: entitaId è il caricamento, non il singolo ordine.
          I dati arrivano tutti da GO, quindi si stampano solo gli ordini che
          in GO esistono — cioè quelli già inviati. */}
      {stampaOrdiniAperta && caricamento && (
        <PrintModal
          societaId={caricamento.societaId}
          entitaType="ordine_go"
          entitaId={caricamento.id}
          entitaLabel={`${ordiniInGo.length} ordini — ${caricamento.etichetta}`}
          onClose={() => setStampaOrdiniAperta(false)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
